import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { FiLogOut, FiDownload, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [stats, setStats] = useState({});
  const [operatingHours, setOperatingHours] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchDashboard();

    // ✨ Polling automatico ogni 5 secondi per aggiornamenti in tempo reale
    const pollingInterval = setInterval(() => {
      console.log('🔄 Polling dashboard...');
      fetchDashboard();
    }, 5000);

    // Cleanup interval al dismount
    return () => clearInterval(pollingInterval);
  }, [user, navigate]);

  useEffect(() => {
    filterByDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, selectedDate, timeSlots]);

  // Rigenerera gli slot quando cambiano la data o gli orari dal backend
  useEffect(() => {
    if (!operatingHours) return;

    // Parsare la data correttamente (selectedDate è formato YYYY-MM-DD)
    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay(); // 0=Domenica, 1=Lunedì, ...
    
    console.log(`📅 Selected Date: ${selectedDate}, Day of Week: ${dayOfWeek}`);
    
    const daySchedule = operatingHours.schedules?.find(s => s.dayOfWeek === dayOfWeek);
    
    console.log(`🔍 Day Schedule found:`, daySchedule);
    
    if (daySchedule && daySchedule.isOpen && daySchedule.timeSlots?.length > 0) {
      const generatedSlots = [];
      
      for (const timeSlot of daySchedule.timeSlots) {
        const slotDuration = timeSlot.slotDuration || 30;
        console.log(`⏱️ Slot Duration: ${slotDuration} min, Time: ${timeSlot.startTime}-${timeSlot.endTime}`);
        const [startHour, startMin] = timeSlot.startTime.split(':').map(Number);
        const [endHour, endMin] = timeSlot.endTime.split(':').map(Number);
        
        let currentHour = startHour;
        let currentMin = startMin;
        
        // Salta la pausa pranzo se impostata
        const breakStartMinutes = daySchedule.hasBreak && daySchedule.breakStart 
          ? (parseInt(daySchedule.breakStart.split(':')[0]) * 60 + parseInt(daySchedule.breakStart.split(':')[1]))
          : null;
        const breakEndMinutes = breakStartMinutes && daySchedule.breakDuration
          ? breakStartMinutes + daySchedule.breakDuration
          : null;
        
        while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
          const currentTotalMinutes = currentHour * 60 + currentMin;
          
          // Salta se durante la pausa pranzo
          if (breakStartMinutes && breakEndMinutes && currentTotalMinutes >= breakStartMinutes && currentTotalMinutes < breakEndMinutes) {
            currentMin += slotDuration;
            if (currentMin >= 60) {
              currentHour += 1;
              currentMin -= 60;
            }
            continue;
          }
          
          const slotStart = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
          const nextMin = currentMin + slotDuration;
          const nextHour = currentHour + Math.floor(nextMin / 60);
          const nextMinMod = nextMin % 60;
          
          if (nextHour > endHour || (nextHour === endHour && nextMinMod > endMin)) {
            break;
          }
          
          const slotEnd = `${String(nextHour).padStart(2, '0')}:${String(nextMinMod).padStart(2, '0')}`;
          generatedSlots.push(`${slotStart} - ${slotEnd}`);
          
          currentHour = nextHour;
          currentMin = nextMinMod;
        }
      }
      
      console.log(`✅ Generated slots count: ${generatedSlots.length}`, generatedSlots);
      setTimeSlots(generatedSlots.length > 0 ? generatedSlots : ['Chiuso']);
    } else {
      console.log(`⚠️ Day closed or no timeSlots`);
      setTimeSlots(['Chiuso']);
    }
  }, [selectedDate, operatingHours]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/admin/dashboard`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setBookings(response.data.appointments || []);
      setStats(response.data.stats || {});
      // Ordina gli schedules per dayOfWeek per garantire ordine consistente
      const sortedOperatingHours = response.data.operatingHours ? {
        ...response.data.operatingHours,
        schedules: (response.data.operatingHours.schedules || []).sort((a, b) => a.dayOfWeek - b.dayOfWeek)
      } : null;
      setOperatingHours(sortedOperatingHours);
      
      setError(null);
    } catch (err) {
      console.error('Errore caricamento dashboard:', err);
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const filterByDate = () => {
    if (!selectedDate) {
      setFilteredBookings(bookings);
      return;
    }
    
    const filtered = bookings.filter(b => {
      const bookingDate = new Date(b.date).toISOString().split('T')[0];
      return bookingDate === selectedDate;
    });
    
    setFilteredBookings(filtered);
  };

  // Raggruppa prenotazioni per fascia oraria (tutte le fasce, populate solo quelle impegnate)
  const groupedBySlot = {};
  timeSlots.forEach(slot => {
    groupedBySlot[slot] = [];
  });
  
  filteredBookings.forEach(booking => {
    // Trovi il vero slot dalla configurazione che corrisponde a questo booking
    const slotDuration = operatingHours?.defaultSlotDuration || 30;
    const [hour, min] = booking.timeSlot.split(':').map(Number);
    const nextMin = min + slotDuration;
    const nextHour = hour + Math.floor(nextMin / 60);
    const nextMinMod = nextMin % 60;
    
    const slotKey = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')} - ${String(nextHour).padStart(2, '0')}:${String(nextMinMod).padStart(2, '0')}`;
    
    // Se il slot calcolato non esiste nei timeSlots configurati, 
    // cerca il slot più vicino che esista
    let targetSlot = slotKey;
    if (!groupedBySlot[slotKey]) {
      // Trova lo slot esistente che inizia a questo orario
      const matchingSlot = timeSlots.find(ts => ts.startsWith(`${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`));
      if (matchingSlot) {
        targetSlot = matchingSlot;
      } else {
        // Fallback: crea lo slot nuovo (comportamento precedente per retrocompatibilità)
        groupedBySlot[slotKey] = [];
        targetSlot = slotKey;
      }
    }
    
    if (!groupedBySlot[targetSlot]) {
      groupedBySlot[targetSlot] = [];
    }
    groupedBySlot[targetSlot].push(booking);
  });

  // Array ordinato di slot con prenotazioni
  const slotRows = Object.entries(groupedBySlot).map(([slot, bookings]) => ({
    slot,
    bookings
  }));

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteAppointment = async (id) => {
    if (!window.confirm('Vuoi eliminare questa prenotazione?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/admin/appointments/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      await fetchDashboard();
    } catch (err) {
      alert('Errore nell\'eliminazione della prenotazione');
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/admin/appointments/${id}/status`,
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      await fetchDashboard();
    } catch (err) {
      alert(`Errore nell'aggiornamento dello stato: ${err.response?.data?.error || err.message}`);
    }
  };

  const handlePreviousDay = () => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() - 1);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const newDate = `${y}-${m}-${d}`;
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + 1);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const newDate = `${y}-${m}-${d}`;
    setSelectedDate(newDate);
  };

  const handleExportPDF = async () => {
    if (timeSlots.length === 0) {
      alert('Nessuno slot disponibile per questa data');
      return;
    }

    try {
      // Import jsPDF
      const { jsPDF } = await import('jspdf');
      
      // Import e auto-registra jspdf-autotable
      await import('jspdf-autotable');

      // Crea il documento
      const doc = new jsPDF('l'); // landscape
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Aggiungi logo in alto a sinistra (se disponibile)
      try {
        const imgData = '/images/logo.svg';
        // Nota: in un vero PDF dovremmo caricare l'immagine, per ora usiamo un placeholder
        doc.setFontSize(10);
        doc.text('🏭 REAL', 15, 12);
      } catch (err) {
        console.warn('Logo non caricabile');
      }

      // Titolo sulla destra
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Rapporto Prenotazioni Giornaliero', pageWidth - 15, 12, { align: 'right' });

      // Data
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Data: ${new Date(selectedDate).toLocaleDateString('it-IT')}`, 15, 22);

      // Crea tabella con TUTTI gli slot
      const headers = ['Fascia Oraria', 'Cliente', 'Destinatario', 'Vettore', 'MPS Rifiuto', 'Tipo', 'Ordine', 'Note'];
      const tableData = [];

      // Per ogni slot, trova o crea riga vuota
      timeSlots.forEach(slot => {
        const bookingForSlot = filteredBookings.find(b => b.timeSlot === slot);
        
        if (bookingForSlot) {
          tableData.push([
            slot,
            bookingForSlot.userCompanyName || '',
            bookingForSlot.destination || '',
            bookingForSlot.origin || '',
            bookingForSlot.material || '',
            bookingForSlot.type === 'carico' ? 'Carico' : 'Scarico',
            '', // Ordine
            bookingForSlot.notes || ''
          ]);
        } else {
          // Slot vuoto
          tableData.push([slot, '', '', '', '', '', '', '']);
        }
      });

      // Usa autoTable se disponibile
      if (doc.autoTable) {
        doc.autoTable({
          head: [headers],
          body: tableData,
          startY: 30,
          margin: { left: 10, right: 10 },
          styles: {
            fontSize: 8,
            cellPadding: 2
          },
          headerStyles: {
            fillColor: [34, 197, 94],
            textColor: [255, 255, 255],
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [240, 253, 244]
          }
        });
      } else {
        // Fallback: tabella manuale
        console.warn('autoTable non disponibile, usa tabella manuale');
        let yPosition = 30;
        const colWidth = (pageWidth - 20) / headers.length;
        
        // Headers
        doc.setFillColor(34, 197, 94);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        headers.forEach((header, i) => {
          doc.rect(10 + i * colWidth, yPosition, colWidth, 5, 'F');
          doc.text(header, 10 + i * colWidth + 1, yPosition + 3);
        });
        
        yPosition += 5;
        
        // Data rows
        doc.setTextColor(0, 0, 0);
        tableData.forEach((row, idx) => {
          if (yPosition > pageHeight - 10) {
            doc.addPage('l');
            yPosition = 10;
          }
          
          row.forEach((cell, i) => {
            const bg = idx % 2 === 0 ? [240, 253, 244] : [255, 255, 255];
            doc.setFillColor(bg[0], bg[1], bg[2]);
            doc.rect(10 + i * colWidth, yPosition, colWidth, 4, 'F');
            doc.text(String(cell), 10 + i * colWidth + 1, yPosition + 2);
          });
          yPosition += 4;
        });
      }

      doc.save(`prenotazioni-${selectedDate}.pdf`);
      console.log('✅ PDF esportato con successo');
    } catch (err) {
      console.error('❌ Errore nell\'esportazione PDF:', err);
      alert('Errore nell\'esportazione del PDF: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white py-6 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-purple-100">{user?.companyName} (Admin)</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg transition"
          >
            <FiLogOut size={20} />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-12">
        {loading && <p className="text-gray-600 text-center">Caricamento dati...</p>}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {!loading && (
          <>
            {/* Statistiche */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div className="bg-white rounded-lg shadow p-4">
                <p className="text-sm text-gray-600">Totale</p>
                <p className="text-2xl font-bold text-gray-800">{stats.total || 0}</p>
              </div>
              <div className="bg-green-50 rounded-lg shadow p-4">
                <p className="text-sm text-green-600">Confermati</p>
                <p className="text-2xl font-bold text-green-800">{stats.confirmed || 0}</p>
              </div>
              <div className="bg-yellow-50 rounded-lg shadow p-4">
                <p className="text-sm text-yellow-600">In Attesa</p>
                <p className="text-2xl font-bold text-yellow-800">{stats.pending || 0}</p>
              </div>
              <div className="bg-blue-50 rounded-lg shadow p-4">
                <p className="text-sm text-blue-600">Completati</p>
                <p className="text-2xl font-bold text-blue-800">{stats.completed || 0}</p>
              </div>
              <div className="bg-red-50 rounded-lg shadow p-4">
                <p className="text-sm text-red-600">Annullati</p>
                <p className="text-2xl font-bold text-red-800">{stats.cancelled || 0}</p>
              </div>
            </div>

            {/* Date Selector */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex flex-col md:flex-row gap-4 md:items-end">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Visualizza giorno
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={handlePreviousDay}
                      className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition flex items-center justify-center"
                      title="Giorno precedente"
                    >
                      <FiChevronLeft size={20} />
                    </button>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      onClick={handleNextDay}
                      className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition flex items-center justify-center"
                      title="Giorno successivo"
                    >
                      <FiChevronRight size={20} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                  <button
                    onClick={handleExportPDF}
                    className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition"
                  >
                    <FiDownload size={20} />
                    <span>Esporta PDF</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin-operating-hours')}
                    className="flex items-center justify-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition"
                  >
                    <span>⏰</span>
                    <span>Gestisci Orari</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin-materials')}
                    className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition"
                  >
                    <span>🏭</span>
                    <span>Gestisci Materiali</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Tabella */}
            {/* Tabella con Fasce Orarie */}
            <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-yellow-400 border-b-2 border-yellow-500">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Fascia Oraria</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Cliente</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Destinatario</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Vettore</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">MPS Rifiuto</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Tipo</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Ordine</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Note</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Stato</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-800">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slotRows.map((row, slotIdx) => (
                      <React.Fragment key={slotIdx}>
                        {row.bookings.length === 0 ? (
                          // Riga vuota con fascia oraria nella prima colonna
                          <tr className="border-t-2 border-t-yellow-400">
                            <td className="px-4 py-3 font-bold text-gray-800">{row.slot}</td>
                            <td colSpan="8" className="px-4 py-3 text-gray-400 italic">— Nessuna prenotazione —</td>
                          </tr>
                        ) : (
                          row.bookings.map((booking, idx) => (
                            <tr 
                              key={booking.id} 
                              className={idx === 0 ? 'border-t-2 border-t-yellow-400' : 'border-b border-gray-200'}
                            >
                              {idx === 0 && (
                                <td className="px-4 py-3 font-bold text-gray-800 border-r border-yellow-400">{row.slot}</td>
                              )}
                              {idx > 0 && (
                                <td className="px-4 py-3"></td>
                              )}
                              <td className="px-4 py-3">{booking.userCompanyName || '-'}</td>
                              <td className="px-4 py-3">{booking.destination}</td>
                              <td className="px-4 py-3">{booking.origin}</td>
                              <td className="px-4 py-3">{booking.material}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  booking.type === 'carico'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {booking.type === 'carico' ? 'Carico' : 'Scarico'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-500">-</td>
                              <td className="px-4 py-3 text-sm">{booking.notes || '-'}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  booking.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : booking.status === 'confirmed'
                                    ? 'bg-green-100 text-green-800'
                                    : booking.status === 'completed'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {booking.status === 'pending' ? 'In Attesa' :
                                   booking.status === 'confirmed' ? 'Confermato' :
                                   booking.status === 'completed' ? 'Completato' :
                                   'Annullato'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  {booking.status === 'pending' && (
                                    <button
                                      onClick={() => handleUpdateStatus(booking.id, 'confirmed')}
                                      className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white text-xs rounded transition"
                                      title="Conferma prenotazione"
                                    >
                                      Conferma
                                    </button>
                                  )}
                                  {booking.status === 'confirmed' && (
                                    <button
                                      onClick={() => handleUpdateStatus(booking.id, 'completed')}
                                      className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded transition"
                                      title="Segna come completato"
                                    >
                                      Completa
                                    </button>
                                  )}
                                  {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                                    <button
                                      onClick={() => {
                                        if (window.confirm('Vuoi annullare questa prenotazione?')) {
                                          handleUpdateStatus(booking.id, 'cancelled');
                                        }
                                      }}
                                      className="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-xs rounded transition"
                                      title="Annulla prenotazione"
                                    >
                                      Annulla
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteAppointment(booking.id)}
                                    className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded transition"
                                    title="Elimina"
                                  >
                                    Elimina
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
